import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { 
  Palette, Plus, Folder, CreditCard, LogOut, Trash2, 
  Eye, Clock, CheckCircle, AlertCircle, Loader2,
  Zap, Calendar, TrendingUp, Crown, Library, Camera
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteProject, setDeleteProject] = useState(null);
  const [usage, setUsage] = useState(null);

  // Gate render on a fully-populated user. Truthy-only checks allow `user` objects
  // rehydrated from localStorage without `subscription_tier` — which was the source
  // of the "undefined Plan" flash on login.
  const isUserReady = !!user && typeof user.subscription_tier === "string" && user.subscription_tier.length > 0;

  // Check for pending subscription after login
  useEffect(() => {
    const pendingPlan = localStorage.getItem('colorwonder_pending_plan');
    if (pendingPlan && user) {
      try {
        const planData = JSON.parse(pendingPlan);
        // Only process if less than 10 minutes old
        const timestamp = new Date(planData.timestamp);
        const now = new Date();
        const diffMinutes = (now - timestamp) / 1000 / 60;
        
        if (diffMinutes < 10) {
          localStorage.removeItem('colorwonder_pending_plan');
          toast.info(`Continuing to ${planData.planId} subscription...`);
          // Redirect to pricing to complete subscription
          navigate(`/pricing?plan=${planData.planId}&annual=${planData.isAnnual}`);
          return;
        }
      } catch (e) {
        console.error("Error parsing pending plan:", e);
      }
      localStorage.removeItem('colorwonder_pending_plan');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchProjects();
    fetchUsage();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get("/projects");
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      if (error.response?.status === 401) {
        // Session expired, let ProtectedRoute handle it
        return;
      }
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await apiClient.get("/usage");
      console.log("Dashboard usage data:", response.data);
      setUsage(response.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
      console.error("Usage error response:", error.response?.data);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else if (error.response?.status === 403) {
        toast.error("Account issue: " + (error.response?.data?.detail || "Access denied"));
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteProject) return;
    
    try {
      await apiClient.delete(`/projects/${deleteProject}`);
      toast.success("Project deleted");
      setProjects(projects.filter(p => p.project_id !== deleteProject));
    } catch (error) {
      toast.error("Failed to delete project");
    } finally {
      setDeleteProject(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: "bg-yellow-100 text-yellow-700", text: "Pending" },
      generating: { icon: Loader2, color: "bg-blue-100 text-blue-700", text: "Generating" },
      completed: { icon: CheckCircle, color: "bg-green-100 text-green-700", text: "Completed" },
      failed: { icon: AlertCircle, color: "bg-red-100 text-red-700", text: "Failed" }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className={`w-3 h-3 ${status === 'generating' ? 'animate-spin' : ''}`} />
        {badge.text}
      </span>
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getPlanIcon = (plan) => {
    const icons = {
      free: Zap,
      basic: CreditCard,
      plus: TrendingUp,
      pro: Crown
    };
    return icons[plan || 'free'] || Zap;
  };

  const getPlanColor = (plan) => {
    const colors = {
      free: "text-slate-600 bg-slate-100",
      basic: "text-blue-600 bg-blue-100",
      plus: "text-violet-600 bg-violet-100",
      pro: "text-amber-600 bg-amber-100"
    };
    return colors[plan || 'free'] || colors.free;
  };

  const dailyProgress = usage ? (usage.pages_used_today / usage.pages_limit_today) * 100 : 0;
  const monthlyProgress = usage ? (usage.pages_used_this_month / usage.pages_limit_this_month) * 100 : 0;

  // Show a full-page skeleton until auth has resolved AND the user object carries a tier.
  // This prevents the "undefined Plan" flash observed between initial render and /auth/me response.
  if (authLoading || !isUserReady) {
    return (
      <div className="min-h-screen bg-hero-gradient" data-testid="dashboard-loading">
        <header className="bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-slate-200 animate-pulse" />
                <div className="h-5 w-32 bg-slate-200 rounded animate-pulse" />
              </div>
              <div className="flex items-center gap-4">
                <div className="h-9 w-28 bg-slate-200 rounded-full animate-pulse" />
                <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse" />
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10">
          <div className="h-8 w-64 bg-slate-200 rounded animate-pulse mb-4" />
          <div className="h-4 w-80 bg-slate-200 rounded animate-pulse mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-40 rounded-3xl" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              {/* Plan Badge - show even when loading with fallback */}
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${getPlanColor(usage?.current_plan || user?.subscription_tier)}`} data-testid="plan-badge">
                {(() => {
                  const PlanIcon = getPlanIcon(usage?.current_plan || user?.subscription_tier);
                  return <PlanIcon className="w-4 h-4" />;
                })()}
                <span className="font-semibold capitalize">
                  {usage?.plan_name || (user?.subscription_tier ? `${user.subscription_tier.charAt(0).toUpperCase()}${user.subscription_tier.slice(1)}` : 'Free')} Plan
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-slate-600"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600">
            Create magical coloring books for your little artists
          </p>
        </div>

        {/* Usage Stats */}
        {usage && (
          <div className="bg-white rounded-3xl p-6 border border-slate-100 mb-8" data-testid="usage-dashboard">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Your Usage
              </h2>
              <Link to="/pricing">
                <Button variant="outline" size="sm" className="rounded-full">
                  {usage.current_plan === "free" ? "Upgrade Plan" : "Manage Plan"}
                </Button>
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Daily Usage */}
              <div className="bg-slate-50 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-600">Today's Usage</span>
                  <span className="text-sm text-slate-500">
                    {usage.pages_used_today} / {usage.pages_limit_today} pages
                  </span>
                </div>
                <Progress value={dailyProgress} className="h-2 mb-2" />
                <p className="text-xs text-slate-500">
                  {usage.pages_remaining_today} pages remaining today
                </p>
              </div>
              
              {/* Monthly Usage */}
              <div className="bg-slate-50 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-600">Monthly Usage</span>
                  <span className="text-sm text-slate-500">
                    {usage.pages_used_this_month} / {usage.pages_limit_this_month} pages
                  </span>
                </div>
                <Progress value={monthlyProgress} className="h-2 mb-2" />
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Calendar className="w-3 h-3" />
                  <span>Resets on {usage.reset_date}</span>
                </div>
              </div>
            </div>

            {/* Limit Warning */}
            {usage.pages_remaining_this_month <= 1 && usage.current_plan === "free" && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-amber-800 font-medium">
                    {usage.pages_remaining_this_month === 0 
                      ? "You've used all your free pages this month!"
                      : `Only ${usage.pages_remaining_this_month} page left this month!`}
                  </p>
                  <p className="text-amber-600 text-sm">
                    Resets on {usage.reset_date} or upgrade for more pages.
                  </p>
                </div>
                <Link to="/pricing">
                  <Button size="sm" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                    Upgrade Now
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <button
            onClick={() => navigate("/generate")}
            disabled={usage && usage.pages_remaining_today === 0}
            className={`bg-gradient-to-br from-violet-600 to-indigo-600 rounded-3xl p-6 text-left text-white transition-all card-hover ${
              usage && usage.pages_remaining_today === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl'
            }`}
            data-testid="create-new-btn"
          >
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-4">
              <Plus className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Create New Book
            </h3>
            <p className="text-violet-100 text-sm">
              {usage && usage.pages_remaining_today === 0 
                ? "Daily limit reached. Try again tomorrow!"
                : `${usage?.pages_remaining_today || 0} pages available today`}
            </p>
          </button>

          <Link
            to="/library"
            className="bg-white rounded-3xl p-6 text-left border border-slate-100 hover:shadow-lg transition-all card-hover"
            data-testid="my-library-btn"
          >
            <div className="w-12 h-12 rounded-2xl bg-pink-100 flex items-center justify-center mb-4">
              <Library className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              My Library
            </h3>
            <p className="text-slate-500 text-sm">
              View all your past creations
            </p>
          </Link>

          <Link
            to="/pricing"
            className="bg-white rounded-3xl p-6 text-left border border-slate-100 hover:shadow-lg transition-all card-hover"
            data-testid="buy-credits-btn"
          >
            <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
              <CreditCard className="w-6 h-6 text-violet-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              {usage?.current_plan === "free" ? "Upgrade Plan" : "Manage Plan"}
            </h3>
            <p className="text-slate-500 text-sm">
              {usage?.current_plan === "free" 
                ? "Get more pages with a paid plan"
                : `Current: ${usage?.plan_name || (user?.subscription_tier ? user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1) : "Free")} Plan`}
            </p>
          </Link>

          <Link
            to="/photo-to-coloring"
            className={`relative rounded-3xl p-6 text-left border transition-all card-hover ${
              usage?.current_plan === "plus" || usage?.current_plan === "pro"
                ? "bg-gradient-to-br from-pink-50 to-violet-50 border-pink-200 hover:shadow-lg"
                : "bg-white border-slate-100 hover:shadow-lg"
            }`}
            data-testid="photo-to-coloring-btn"
          >
            {usage?.current_plan !== "plus" && usage?.current_plan !== "pro" && (
              <div className="absolute top-3 right-3 px-2 py-1 bg-gradient-to-r from-violet-500 to-pink-500 rounded-full">
                <span className="text-xs font-medium text-white">Premium</span>
              </div>
            )}
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-100 to-violet-100 flex items-center justify-center mb-4">
              <Camera className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Photo to Coloring
            </h3>
            <p className="text-slate-500 text-sm">
              {usage?.current_plan === "plus" || usage?.current_plan === "pro"
                ? "Convert photos to coloring pages"
                : "Upgrade to Plus or Pro to unlock"}
            </p>
          </Link>
        </div>

        {/* Projects Grid */}
        <div>
          <h2 className="text-2xl font-semibold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Your Projects
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-64 rounded-3xl" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Folder className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                No projects yet
              </h3>
              <p className="text-slate-500 mb-6">
                Create your first coloring book to get started!
              </p>
              <Button
                onClick={() => navigate("/generate")}
                disabled={usage && usage.pages_remaining_today === 0}
                className="h-12 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold btn-lift"
                data-testid="empty-create-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Book
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div
                  key={project.project_id}
                  className="bg-white rounded-3xl overflow-hidden border border-slate-100 card-hover"
                  data-testid={`project-card-${project.project_id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-violet-50 to-pink-50 flex items-center justify-center">
                    <Palette className="w-12 h-12 text-violet-300" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-800 truncate" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                        {project.theme}
                      </h3>
                      {getStatusBadge(project.status)}
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      {project.age_group} • {project.style} • {project.page_count || 10} pages
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/preview/${project.project_id}`)}
                        className="flex-1 rounded-full"
                        disabled={project.status !== "completed"}
                        data-testid={`view-project-${project.project_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteProject(project.project_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-project-${project.project_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProject} onOpenChange={() => setDeleteProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your coloring book project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
